import { Phone, MessageSquare, Zap } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { PHONE, PHONE_TEL } from "@/lib/constants";

/**
 * "Meet JD Dean" bio strip — placed above the footer.
 * Contains the big shield logo + name + credentials + contact.
 */
export const JdBio = () => {
  const [photoOpen, setPhotoOpen] = useState(false);

  return (
    <section
      data-testid="jd-bio"
      className="relative overflow-hidden border-y-2 border-red-600"
      style={{
        background:
          "radial-gradient(circle at 22% 35%, rgba(30,58,138,0.55) 0%, transparent 55%), radial-gradient(circle at 80% 65%, rgba(220,38,38,0.32) 0%, transparent 55%), linear-gradient(180deg, #020617 0%, #0b1220 60%, #020617 100%)",
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-sky-300 font-bold">
            Meet Your Inspector
          </div>
          <h2 className="font-impact uppercase text-white text-3xl sm:text-4xl tracking-tight mt-2">
            The Face Behind The Roof.
          </h2>
        </div>

        <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-center max-w-4xl mx-auto">
          {/* Logo (clickable opens JD photo) */}
          <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid="jd-bio-logo"
                aria-label="View JD's photo"
                className="shrink-0 group [perspective:800px] mx-auto"
              >
                <div
                  className="relative transition-transform duration-300 ease-out group-hover:[transform:rotateX(8deg)_rotateY(-10deg)_scale(1.05)] group-active:scale-95"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-0 -m-4 rounded-full pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 50%, rgba(125,211,252,0.55) 0%, rgba(220,38,38,0.35) 38%, rgba(0,0,0,0) 70%)",
                      filter: "blur(18px)",
                    }}
                  />
                  <img
                    src="/sdp-logo.png"
                    alt="Storm Damage Pros — SDP"
                    className="relative h-40 sm:h-52 w-auto"
                    style={{
                      clipPath: "circle(49% at 50% 50%)",
                      filter:
                        "drop-shadow(0 8px 18px rgba(0,0,0,0.75)) drop-shadow(0 0 16px rgba(125,211,252,0.5)) drop-shadow(0 0 8px rgba(220,38,38,0.45))",
                    }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-30 mix-blend-screen"
                    style={{
                      clipPath: "circle(49% at 50% 50%)",
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.15) 100%)",
                    }}
                  />
                </div>
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="jd-photo-modal"
              className="max-w-2xl w-[92vw] p-0 bg-slate-950 border-red-600 border-2 rounded-none overflow-hidden"
            >
              <img
                src="/jd-photo-full.png"
                alt="JD Dean — Storm Damage Specialist"
                className="w-full h-auto block"
              />
              <div className="px-6 py-5 bg-slate-950 border-t border-slate-800">
                <div className="font-display font-black uppercase text-xl text-white">JD Dean</div>
                <div className="text-xs tracking-[0.2em] uppercase text-red-500 mt-1">
                  Storm Specialist
                </div>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="mt-3 inline-flex items-center gap-2 font-display font-bold text-white text-lg hover:text-sky-300 transition-colors"
                >
                  <Phone size={16} className="text-red-600" /> {PHONE}
                </a>
              </div>
            </DialogContent>
          </Dialog>

          {/* Credentials block */}
          <div className="text-center md:text-left relative md:pl-6">
            <span
              aria-hidden
              className="hidden md:block absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-red-600"
              style={{ boxShadow: "0 0 12px rgba(220,38,38,0.7)" }}
            />
            <div
              className="font-impact uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight"
              style={{
                background:
                  "linear-gradient(180deg, #ffffff 0%, #e2e8f0 55%, #94a3b8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter:
                  "drop-shadow(0 2px 0 rgba(0,0,0,0.6)) drop-shadow(0 0 12px rgba(125,211,252,0.25))",
              }}
            >
              JD Dean
            </div>
            <div className="mt-3 inline-flex items-center gap-2 bg-red-600/15 border border-red-600/60 px-3 py-1.5">
              <Zap size={13} className="text-red-500 fill-red-500" />
              <span className="text-[12px] uppercase tracking-[0.28em] text-white font-bold">
                Storm Specialist
              </span>
            </div>
            <div className="text-sm sm:text-base leading-snug uppercase tracking-wide text-slate-200 font-bold mt-4">
              Weather &amp; Storm Damage Expert · Roof Inspector
            </div>
            <div className="text-sm sm:text-base leading-snug uppercase tracking-wide text-slate-200 font-bold">
              Certified Roofing Specialist · Insurance Claims Assistant
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="jd-bio-call"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-impact uppercase tracking-wider px-5 py-3 text-lg border-2 border-white transition-colors"
              >
                <Phone size={18} /> {PHONE}
              </a>
              <a
                href={`sms:${PHONE_TEL}`}
                data-testid="jd-bio-text"
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white font-impact uppercase tracking-wider px-5 py-3 text-lg border-2 border-white transition-colors"
              >
                <MessageSquare size={18} /> Text JD
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* barber stripe top edge */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-1"
        style={{
          background:
            "repeating-linear-gradient(90deg, #dc2626 0 28px, #ffffff 28px 32px, #020617 32px 60px, #1e3a8a 60px 64px)",
        }}
      />
    </section>
  );
};
